import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CoordioDropdown, Input, Switch, Textarea } from '@coordio/ui';
import { LoadingSkeleton } from '@coordio/ui/layout';
import { Save as SaveIcon, ArrowLeft as BackIcon } from 'lucide-react';
import { externalSystemService } from '../../services/externalSystemService';
import { useNotificationStore } from '../../store/notificationStore';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const RISK_LEVELS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Critical', label: 'Critical' },
];

interface ApiMethodFormProps {
  systemId: string;
  methodId?: string;
  onSuccess?: () => void;
}

/**
 * API method form component for creating and editing API methods.
 */
export const ApiMethodForm = ({ systemId, methodId, onSuccess }: ApiMethodFormProps) => {
  const navigate = useNavigate();
  const isEdit = !!methodId;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [httpMethod, setHttpMethod] = useState('GET');
  const [endpointPath, setEndpointPath] = useState('');
  const [inputSchemaJson, setInputSchemaJson] = useState('{}');
  const [outputSchemaJson, setOutputSchemaJson] = useState('');
  const [toolName, setToolName] = useState('');
  const [toolDescription, setToolDescription] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [riskLevel, setRiskLevel] = useState('Low');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && methodId) {
      loadMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodId, isEdit]);

  const loadMethod = async () => {
    if (!methodId) return;
    setLoading(true);
    setError(null);
    try {
      const methods = await externalSystemService.getApiMethods(systemId);
      const method = methods.find((m) => m.id === methodId);
      if (method) {
        setName(method.name);
        setDescription(method.description || '');
        setHttpMethod(method.httpMethod);
        setEndpointPath(method.endpointPath);
        setToolName(method.toolName);
        setToolDescription(method.toolDescription);
        setRequiresApproval(method.requiresApproval);
        setRiskLevel(method.riskLevel);
        setIsActive(method.isActive);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load API method';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!endpointPath.trim()) {
      setError('Endpoint path is required');
      return;
    }

    if (!toolName.trim()) {
      setError('Tool name is required');
      return;
    }

    if (inputSchemaJson.trim()) {
      try {
        JSON.parse(inputSchemaJson.trim());
      } catch {
        setError('Input schema JSON must be valid JSON');
        return;
      }
    }

    if (outputSchemaJson.trim()) {
      try {
        JSON.parse(outputSchemaJson.trim());
      } catch {
        setError('Output schema JSON must be valid JSON');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        httpMethod,
        endpointPath: endpointPath.trim(),
        inputSchemaJson: inputSchemaJson.trim() || '{}',
        outputSchemaJson: outputSchemaJson.trim() || undefined,
        toolName: toolName.trim(),
        toolDescription: toolDescription.trim() || '',
        requiresApproval,
        riskLevel,
        isActive,
      };

      if (isEdit && methodId) {
        await externalSystemService.updateApiMethod(systemId, methodId, data);
        addNotification('API method updated successfully', 'success');
      } else {
        await externalSystemService.createApiMethod(systemId, data);
        addNotification('API method created successfully', 'success');
      }

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/external-systems/${systemId}`);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save API method';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="ghost" onClick={() => navigate(`/external-systems/${systemId}`)}>
          <BackIcon className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h5 className="text-xl font-semibold">{isEdit ? 'Edit API Method' : 'Create API Method'}</h5>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400 mb-2">
          <span className="flex-1">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Name<span className="text-red-500">*</span></label>
              <Input className="w-full" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">HTTP Method</label>
              <CoordioDropdown
                options={HTTP_METHODS.map((m) => ({ label: m, value: m }))}
                value={httpMethod}
                onChange={(v) => setHttpMethod(v ?? 'GET')}
                clearable={false}
              />
            </div>
          </div>

          <div className="col-span-12">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Description</label>
              <Textarea className="w-full" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>

          <div className="col-span-12">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Endpoint Path<span className="text-red-500">*</span></label>
              <Input className="w-full" required value={endpointPath} onChange={(e) => setEndpointPath(e.target.value)} />
              <p className="text-xs text-[var(--color-text-secondary)]">Path relative to base URL (e.g., /api/v1/users or /api/v1/users/{'{id}'})</p>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Tool Name<span className="text-red-500">*</span></label>
              <Input className="w-full" required value={toolName} onChange={(e) => setToolName(e.target.value)} />
              <p className="text-xs text-[var(--color-text-secondary)]">Unique name for the tool (used by agents)</p>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Risk Level</label>
              <CoordioDropdown
                options={RISK_LEVELS}
                value={riskLevel}
                onChange={(v) => setRiskLevel(v ?? 'Low')}
                clearable={false}
              />
            </div>
          </div>

          <div className="col-span-12">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Tool Description<span className="text-red-500">*</span></label>
              <Textarea className="w-full" required rows={2} value={toolDescription} onChange={(e) => setToolDescription(e.target.value)} />
              <p className="text-xs text-[var(--color-text-secondary)]">Description of what this tool does (used by agents)</p>
            </div>
          </div>

          <div className="col-span-12">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Input Schema JSON</label>
              <Textarea className="w-full" rows={6} value={inputSchemaJson} onChange={(e) => setInputSchemaJson(e.target.value)} />
              <p className="text-xs text-[var(--color-text-secondary)]">JSON schema for input parameters (OpenAPI/JSON Schema format)</p>
            </div>
          </div>

          <div className="col-span-12">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Output Schema JSON</label>
              <Textarea className="w-full" rows={4} value={outputSchemaJson} onChange={(e) => setOutputSchemaJson(e.target.value)} />
              <p className="text-xs text-[var(--color-text-secondary)]">JSON schema for output response (optional)</p>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={requiresApproval} onCheckedChange={setRequiresApproval} />
              <span className="text-sm">Requires Approval</span>
            </label>
          </div>

          <div className="col-span-12 sm:col-span-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <span className="text-sm">Active</span>
            </label>
          </div>

          <div className="col-span-12">
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => navigate(`/external-systems/${systemId}`)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                <SaveIcon className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
